import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { fetchMyTours, fetchMyRegisteredTours } from '../../services/tourService';
import { TourDetailModal } from '../Tours/TourDetailModal';
import './ProfileCalendar.css';

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

function toDate(ts) {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  const d = new Date(ts);
  return isNaN(d) ? null : d;
}
const dkey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const fmtTime = (d) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const ROLE = {
  organizing: { label: 'Organizing', dot: 'org' },
  riding:     { label: 'Riding',     dot: 'ride' },
  tour:       { label: 'Tour',       dot: 'org' },
};

/**
 * Month calendar of tours/events.
 * - Own profile (no `events` prop): fetches the user's organizing + riding tours.
 * - Public profile (`events` prop, may be null while loading): renders the
 *   supplied list (each item should carry `_role`).
 */
export function ProfileCalendar({ events: eventsProp } = {}) {
  const { user } = useAuth();
  const controlled = eventsProp !== undefined;
  const [fetched, setFetched] = useState(null);
  const [view, setView] = useState(() => { const n = new Date(); return { y: n.getFullYear(), m: n.getMonth() }; });
  const [sel, setSel]   = useState(() => dkey(new Date()));
  const [openTour, setOpenTour] = useState(null);

  useEffect(() => {
    if (controlled) return;
    if (!user) { setFetched([]); return; }
    Promise.all([
      fetchMyTours(user.uid).catch(() => []),
      fetchMyRegisteredTours(user.uid).catch(() => []),
    ]).then(([org, rid]) => {
      const byId = new Map();
      (org || []).forEach(t => byId.set(t.id, { ...t, _role: 'organizing' }));
      (rid || []).forEach(t => { if (!byId.has(t.id)) byId.set(t.id, { ...t, _role: 'riding' }); });
      setFetched([...byId.values()]);
    });
  }, [user, controlled]);

  const tours = controlled ? eventsProp : fetched;

  const byDay = useMemo(() => {
    const m = {};
    (tours || []).forEach(t => {
      const d = toDate(t.startDate);
      if (!d) return;
      (m[dkey(d)] ||= []).push({ ...t, _date: d });
    });
    return m;
  }, [tours]);

  // Month grid (Monday-start, padded to full weeks).
  const first = new Date(view.y, view.m, 1);
  const startOffset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const todayKey = dkey(new Date());
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(view.y, view.m, d));
  while (cells.length % 7 !== 0) cells.push(null);

  const prev = () => setView(v => (v.m === 0 ? { y: v.y - 1, m: 11 } : { y: v.y, m: v.m - 1 }));
  const next = () => setView(v => (v.m === 11 ? { y: v.y + 1, m: 0 } : { y: v.y, m: v.m + 1 }));

  const selList = (byDay[sel] || []).slice().sort((a, b) => a._date - b._date);
  const rolesPresent = [...new Set((tours || []).map(t => t._role || 'tour'))].filter(r => ROLE[r]);

  return (
    <div className="pc">
      <div className="pc__nav">
        <button className="pc__nav-btn" onClick={prev} aria-label="Previous month">‹</button>
        <span className="pc__month">{MONTHS[view.m]} {view.y}</span>
        <button className="pc__nav-btn" onClick={next} aria-label="Next month">›</button>
      </div>

      <div className="pc__grid pc__grid--head">
        {WEEKDAYS.map(w => <span key={w} className="pc__wd">{w}</span>)}
      </div>
      <div className="pc__grid">
        {cells.map((d, i) => {
          if (!d) return <span key={i} className="pc__cell pc__cell--empty" />;
          const k = dkey(d);
          const evs = byDay[k];
          const roles = evs ? new Set(evs.map(e => e._role)) : null;
          return (
            <button
              key={i}
              className={`pc__cell${k === sel ? ' is-sel' : ''}${k === todayKey ? ' is-today' : ''}${evs ? ' has-ev' : ''}`}
              onClick={() => setSel(k)}
            >
              <span className="pc__num">{d.getDate()}</span>
              <span className="pc__dots">
                {roles && [...roles].map(r => (
                  <i key={r} className={`pc__dot pc__dot--${ROLE[r]?.dot || 'org'}`} />
                ))}
              </span>
            </button>
          );
        })}
      </div>

      {rolesPresent.length > 0 && (
        <div className="pc__legend">
          {rolesPresent.map(r => (
            <span key={r}><i className={`pc__dot pc__dot--${ROLE[r].dot}`} /> {ROLE[r].label}</span>
          ))}
        </div>
      )}

      <div className="pc__list">
        {tours === null ? (
          <div className="pc__empty">Loading…</div>
        ) : selList.length === 0 ? (
          <div className="pc__empty">No tours on this day.</div>
        ) : selList.map(t => (
          <button key={t.id} className="pc__item" onClick={() => setOpenTour(t)}>
            {t.imageUrls?.[0]
              ? <img className="pc__item-thumb" src={t.imageUrls[0]} alt="" loading="lazy" />
              : <span className="pc__item-thumb pc__item-thumb--ph">{t.name?.[0]?.toUpperCase() ?? 'T'}</span>}
            <span className="pc__item-body">
              <span className="pc__item-name">{t.name}</span>
              <span className="pc__item-meta">
                {fmtTime(t._date)}
                <span className={`pc__item-role pc__item-role--${t._role || 'tour'}`}>{ROLE[t._role]?.label || 'Tour'}</span>
              </span>
            </span>
            <svg className="pc__item-chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        ))}
      </div>

      {openTour && (
        <TourDetailModal
          tour={openTour}
          onClose={() => setOpenTour(null)}
          onUpgrade={() => setOpenTour(null)}
          zIndex={1700}
        />
      )}
    </div>
  );
}
